import React, { useState } from 'react';
import { 
  X, 
  CheckSquare, 
  CheckCircle2, 
  XCircle, 
  Star, 
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  Layers,
  Sparkles,
  Trash2
} from 'lucide-react';
import { 
  StepTest, 
  SteadyStateChecklist, 
  YesNo, 
  PassFail,
  ManipulatedVariable
} from '../types';

interface StepEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  step: StepTest;
  mv?: ManipulatedVariable;
  onEvaluate: (evaluatedStep: StepTest) => void;
  onDeleteStep?: (stepId: string) => Promise<void> | void;
}

export const StepEvaluationModal: React.FC<StepEvaluationModalProps> = ({
  isOpen,
  onClose,
  step,
  mv,
  onEvaluate,
  onDeleteStep,
}) => {
  // Steady-State Evaluation Checklist
  const [checklist, setChecklist] = useState<SteadyStateChecklist>({
    heldRequiredDuration: true,
    noOperatorIntervention: true,
    noEquipmentChanges: true,
    noMajorFeedChanges: true,
    analyzerResponseObserved: true,
    stableResponseObserved: true,
  });

  const [steadyStateAchieved, setSteadyStateAchieved] = useState<YesNo>('Yes');
  const [disturbancesPresent, setDisturbancesPresent] = useState<YesNo>('No');
  const [analyzerResponseObserved, setAnalyzerResponseObserved] = useState<YesNo>('Yes');
  const [dataQualityRating, setDataQualityRating] = useState<number>(5);
  const [comments, setComments] = useState<string>(step.comments || '');

  if (!isOpen) return null;

  const handleChecklistToggle = (key: keyof SteadyStateChecklist) => {
    setChecklist((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      if (key === 'analyzerResponseObserved') {
        setAnalyzerResponseObserved(updated.analyzerResponseObserved ? 'Yes' : 'No');
      }
      return updated;
    });
  };

  const computedRecommendation: PassFail = 
    checklist.heldRequiredDuration &&
    checklist.noOperatorIntervention &&
    checklist.noEquipmentChanges &&
    checklist.noMajorFeedChanges &&
    checklist.stableResponseObserved &&
    steadyStateAchieved === 'Yes' &&
    disturbancesPresent === 'No'
      ? 'PASS'
      : 'FAIL';

  const handleFinalize = (decision: 'PASS' | 'FAIL') => {
    const evaluated: StepTest = {
      ...step,
      checklist,
      steadyStateAchieved,
      disturbancesPresent,
      analyzerResponseObserved,
      dataQualityRating,
      comments: comments.trim(),
      status: decision,
      completedAt: new Date().toISOString(),
    };

    onEvaluate(evaluated);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/40 backdrop-blur-xs overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden my-4 sm:my-8 animate-in fade-in-50 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-linear-to-r from-amber-50/50 via-white to-sky-50/50">
          <div className="flex items-center gap-2.5">
            <span className="w-3 h-3 rounded-full bg-amber-500 animate-ping"></span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-slate-900 text-base">Step Test Evaluation & Sign-off</h2>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  Awaiting Operator Sign-off
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Settling period completed for <strong className="text-slate-800 font-mono">{step.mvTag}</strong>. Evaluate steady state before qualifying this move.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 max-h-[82vh] overflow-y-auto">
          {/* Step Execution Summary Card */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-sky-600" />
                <span className="font-bold text-slate-900 font-mono text-sm">{step.mvTag}</span>
                <span className="text-xs text-slate-500 truncate max-w-[200px] sm:max-w-xs">{mv?.description}</span>
              </div>
              <span className="text-[11px] font-mono text-slate-500">
                {step.timestamp}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-[10px] uppercase font-semibold text-slate-400 block">Direction</span>
                <div className="flex items-center gap-1 font-bold font-mono text-slate-800 mt-0.5">
                  {step.direction === '+' ? (
                    <span className="text-emerald-700 flex items-center gap-0.5">
                      <ArrowUpRight className="w-4 h-4" /> + Step Up
                    </span>
                  ) : (
                    <span className="text-rose-600 flex items-center gap-0.5">
                      <ArrowDownRight className="w-4 h-4" /> - Step Down
                    </span>
                  )}
                </div>
              </div>

              <div>
                <span className="text-[10px] uppercase font-semibold text-slate-400 block">Baseline ➔ Target</span>
                <div className="font-bold font-mono text-slate-800 mt-0.5">
                  {step.baselineSp} ➔ {step.targetSp} <span className="font-normal text-slate-500">{mv?.units}</span>
                </div>
              </div>

              <div>
                <span className="text-[10px] uppercase font-semibold text-slate-400 block">Hold Duration</span>
                <div className="font-bold font-mono text-slate-800 mt-0.5 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span>
                    {step.holdDuration >= 60 
                      ? `${step.holdDuration} min (${+(step.holdDuration / 60).toFixed(1)}h)` 
                      : `${step.holdDuration} min`}
                  </span>
                </div>
              </div>

              <div>
                <span className="text-[10px] uppercase font-semibold text-slate-400 block">Personnel</span>
                <div className="text-slate-700 mt-0.5 truncate text-[11px]">
                  {step.engineer} / {step.operator}
                </div>
              </div>
            </div>
          </div>

          {/* Steady State Checklist */}
          <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 font-semibold text-xs text-slate-900">
                <CheckSquare className="w-4 h-4 text-sky-600" />
                <span>Steady-State Criteria Checklist</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Evaluation:</span>
                <span
                  className={`px-3 py-0.5 rounded-full text-xs font-bold tracking-wider ${
                    computedRecommendation === 'PASS'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-amber-600 text-white'
                  }`}
                >
                  {computedRecommendation}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <label className="flex items-center gap-2 text-slate-700 cursor-pointer p-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={checklist.heldRequiredDuration}
                  onChange={() => handleChecklistToggle('heldRequiredDuration')}
                  className="rounded border-slate-300 text-sky-600 focus:ring-0 w-4 h-4"
                />
                <span>Held required settling duration</span>
              </label>

              <label className="flex items-center gap-2 text-slate-700 cursor-pointer p-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={checklist.noOperatorIntervention}
                  onChange={() => handleChecklistToggle('noOperatorIntervention')}
                  className="rounded border-slate-300 text-sky-600 focus:ring-0 w-4 h-4"
                />
                <span>No operator manual trim</span>
              </label>

              <label className="flex items-center gap-2 text-slate-700 cursor-pointer p-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={checklist.noEquipmentChanges}
                  onChange={() => handleChecklistToggle('noEquipmentChanges')}
                  className="rounded border-slate-300 text-sky-600 focus:ring-0 w-4 h-4"
                />
                <span>No equipment / pump swaps</span>
              </label>

              <label className="flex items-center gap-2 text-slate-700 cursor-pointer p-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={checklist.noMajorFeedChanges}
                  onChange={() => handleChecklistToggle('noMajorFeedChanges')}
                  className="rounded border-slate-300 text-sky-600 focus:ring-0 w-4 h-4"
                />
                <span>No major upstream feed swings</span>
              </label>

              <label className="flex items-center gap-2 text-slate-700 cursor-pointer p-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={checklist.analyzerResponseObserved}
                  onChange={() => handleChecklistToggle('analyzerResponseObserved')}
                  className="rounded border-slate-300 text-sky-600 focus:ring-0 w-4 h-4"
                />
                <span>Analyzer response observed</span>
              </label>

              <label className="flex items-center gap-2 text-slate-700 cursor-pointer p-1.5 rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={checklist.stableResponseObserved}
                  onChange={() => handleChecklistToggle('stableResponseObserved')}
                  className="rounded border-slate-300 text-sky-600 focus:ring-0 w-4 h-4"
                />
                <span>Stable new steady-state reached</span>
              </label>
            </div>

            {/* Evaluation Dropdowns & Star Rating */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100 text-xs">
              <div>
                <label className="block text-slate-600 font-medium mb-1">Steady State Achieved?</label>
                <select
                  value={steadyStateAchieved}
                  onChange={(e) => setSteadyStateAchieved(e.target.value as YesNo)}
                  className="w-full bg-slate-50 text-slate-900 border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-600 font-medium mb-1">Disturbances Present?</label>
                <select
                  value={disturbancesPresent}
                  onChange={(e) => setDisturbancesPresent(e.target.value as YesNo)}
                  className="w-full bg-slate-50 text-slate-900 border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
                >
                  <option value="No">No (Clean Test)</option>
                  <option value="Yes">Yes (Upset Occurred)</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-600 font-medium mb-1">Data Quality (1 - 5)</label>
                <div className="flex items-center gap-1 mt-1.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setDataQualityRating(star)}
                      className="p-0.5 text-amber-500 hover:scale-110 transition-transform"
                    >
                      <Star
                        className={`w-4 h-4 ${
                          dataQualityRating >= star ? 'fill-amber-400 text-amber-500' : 'text-slate-300'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="text-[11px] font-mono text-slate-500 ml-1.5 font-semibold">
                    {dataQualityRating}/5
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Comments & Observations */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Comments & Observations
            </label>
            <textarea
              rows={2}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="e.g. Clean first order response observed. Distillate analyzer responded after 45 minutes."
              className="w-full bg-white text-slate-900 text-xs border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 focus:outline-none"
            />
          </div>

          {/* Finalize Decision Buttons */}
          <div className="pt-3 border-t border-slate-200">
            <div className="text-xs font-semibold text-slate-700 mb-2">Declare Test Result:</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleFinalize('PASS')}
                className="flex items-center justify-center gap-2 p-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs shadow-sm transition-all hover:shadow"
              >
                <CheckCircle2 className="w-4 h-4" />
                <div className="text-left">
                  <div className="font-bold">Declare PASS (Valid Move)</div>
                  <div className="text-[10px] text-emerald-100 font-normal">
                    Increments valid move count towards 20 moves
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleFinalize('FAIL')}
                className="flex items-center justify-center gap-2 p-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs shadow-sm transition-all hover:shadow cursor-pointer"
              >
                <XCircle className="w-4 h-4" />
                <div className="text-left">
                  <div className="font-bold">Declare FAIL (Rejected Move)</div>
                  <div className="text-[10px] text-rose-100 font-normal">
                    Disqualifies move due to disturbance or poor step response
                  </div>
                </div>
              </button>
            </div>

            {/* Option to cancel / discard step entirely */}
            {onDeleteStep && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to discard and completely delete this step test for ${step.mvTag}?`)) {
                      onDeleteStep(step.id);
                      onClose();
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-rose-700 hover:text-rose-800 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
                  title="Discard this test without recording pass or fail"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Discard / Delete Step Test</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
